import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { API, ClientDTO, Order, shared_Client_retrieveAccessibleClients_order } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { ModalNavigationComponent } from 'src/app/services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from 'src/app/services/gui/modal/modal-navigation.service';
import { UtilService } from 'src/app/services/util.service';
import { SbSplitButton } from '../../services/global/global-interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { iif } from 'rxjs';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../services/transformation-helper/string-transformation.service';
import { RequestClass } from '../../services/global/RequestClass';

type SplitButtonAction = 'select';

@Component({
  selector: 'sb-accessible-clients',
  templateUrl: './sb-accessible-clients.component.html',
  styleUrls: ['./sb-accessible-clients.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SbAccessibleClientsComponent extends RequestClass implements OnInit, ModalNavigationComponent {
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly translate = inject(TranslateService);
  private readonly api = inject(API);
  private readonly modalNavigation = inject(ModalNavigationService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() isInIntegratedContext: boolean;

  @Output() clientSelected: EventEmitter<ClientDTO> = new EventEmitter<ClientDTO>();

  // global variables
  isInModalContext: boolean;
  modalData: any;

  // table wrapper variables
  clients: ClientDTO[] = [];
  orderDirection: string;
  isClientSelected: boolean = false;

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  ngOnInit() {
    if (this.isInModalContext) {
      this.modalData = this.modalNavigation.getCurrentNavigation().data;
      this.filterConfig.ignoreQueryParams = true;
    }

    this.orderDirection = 'asc';
  }

  select(client: ClientDTO) {
    if (client) {
      this.isClientSelected = true;
    }
    if (this.isInIntegratedContext) {
      this.clientSelected.emit(client);
    }
    if (this.isInModalContext) {
      this.modalNavigation.result(client);
    }
  }

  toggleOrderDirection() {
    if (this.orderDirection === 'desc') {
      this.orderDirection = 'asc';
    } else {
      this.orderDirection = 'desc';
    }
    this.requestDebounced();
  }

  getOrderOptions(): { by: shared_Client_retrieveAccessibleClients_order; param?: string; direction: Order } {
    if (this.orderDirection) {
      return {
        by: shared_Client_retrieveAccessibleClients_order.NAME,
        direction: this.orderDirection === 'asc' ? Order.asc : Order.desc,
      };
    }
    return null;
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = iif(
      () => !this.modalData.connectedClients,
      this.api.shared_Client_retrieveAccessibleClients(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          order: this.getOrderOptions(),
        },
        this.pagination
      ),
      this.api.shared_Client_retrieveConnectedClients(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
    )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (clientList: ClientDTO[]) => {
          this.clients = this.buildSplitButtons(clientList);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  buildSplitButtons<T>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'checked',
        action: 'select',
        tooltip: 'lang.globals.select',
      });
    });
    return result;
  }

  resolveSplitButtonAction(client: ClientDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'select':
        this.select(client);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
